import React, { useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/app-header';
import { AppSideNav } from '../components/app-side-nav';
import { AppFooter } from '../components/app-footer';
import { EventNotificationService, useEventNotification } from '../framework/services/event-notification';
import { AppPages } from './app-pages';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSinglePageAppStyles } from '../app-core/app-styles';
import { mergeStyles } from '@fluentui/react';
import { AppMenuEvents } from '../events/app-menu';
import { BwjSemiFlexContainer } from '../framework/components/bwj-semi-flex-container';
import { BwjScrollContainer } from '../framework/components/bwj-scroll-container';
import { BwjViewManager, BwjViewState } from '../framework/components/bwj-view-manager';
import { BwjButtonType } from '../framework/components/bwj-button';
import { ShopAppEvents } from '../app-shop/events';
import { BwjEventArgs } from '../framework/entities/bwj-event';

const styles = getSinglePageAppStyles();

const subscriberId = 'Component.AppContent';
const resetMainContentScrollEvent = `${subscriberId}.ResetMainContentScroll`

export const AppContent: React.FunctionComponent = () => {
  const [showViews, setShowViews] = useState(false);
  const [menuAvailable, setMenuAvailable] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Loading application...');
  const [showDesktopMenu, setShowDesktopMenu] = useState(true);
  const [invokeShowMobileMenu, setInvokeShowMobileMenu] = useState<{} | undefined>(undefined);
  const [scrollResetInvocation, setScrollResetInvocation] = useState<BwjEventArgs<null> | undefined>(undefined);

  const showLoadingView = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }
  
  useEventNotification(() => setMenuAvailable(true),[], AppMenuEvents.MenuAvailable, subscriberId);
  useEventNotification(() => setMenuAvailable(false),[], AppMenuEvents.MenuUnavailable, subscriberId);
  useEventNotification(() => setShowMenu(!showMenu), [showMenu], AppMenuEvents.ToggleMenu, subscriberId);
  useEventNotification(() => setScrollResetInvocation(new BwjEventArgs(null)), [], resetMainContentScrollEvent, subscriberId);
  useEventNotification((msg) => showLoadingView(msg), [], ShopAppEvents.ShowAppLoadingWithMessage, subscriberId);
  useEventNotification(() => setViewState(BwjViewState.Ready), [], ShopAppEvents.ShowAppReady, subscriberId);
  
  useEffect(() => {
    // only ever do this once.  We do this to ensure that no app view can fire a Menu Available / Unavailable event
    // before this parent component has subscribed to it
    if(!showViews) {
      window.setTimeout(() => {
        setShowViews(true);
        setViewState(BwjViewState.Ready);
      }, 100);
    }
  });

  const appBodyClass = mergeStyles([styles.root]);
  // const mainSectionClass = mergeStyles([styles.main]);
  const contentAreaClass = mergeStyles([styles.contentArea]);

  const onRestartApp = () => {
    window.location.reload();
  }

  return (
    <Router>
      <div className={appBodyClass}>
        <AppHeader
          showMenu={menuAvailable}
          showCart={false}
          showCustomerAccount={false}
          onDesktopMenuButtonClick={() => setShowDesktopMenu(!showDesktopMenu)}
          onMobileMenuButtonClick={() => setInvokeShowMobileMenu({})}
        />
        <BwjSemiFlexContainer
          styles={{ root: { flexGrow: 1 }, content: { display: 'flex', flexDirection: 'row', alignItems: 'stretch' } }}
          pinWidth={false}
          pinHeight={true}
        >
          {menuAvailable &&
          <AppSideNav
            title={'App Title'}
            invokeShowMobileNav={invokeShowMobileMenu}
            showDesktopNav={showDesktopMenu}
          />}
          <section className={contentAreaClass}>
            <BwjViewManager
              state={viewState}
              loadingState={{
                message: loadingMessage.current,
              }}
              errorState={{
                icon: 'StatusErrorFull',
                message: 'We could not start the app due to an error - try restarting the app.  Please be sure to check your Internet connection',
                buttons: [{
                  buttonStyle: BwjButtonType.PrimaryStandard,
                  text: 'Restart app',
                  onClick: onRestartApp,
                }],
              }}
            >
              <BwjScrollContainer
                containerStyle={{ display: 'flex', flexDirection: 'column' }}
                invokeScrollReset={scrollResetInvocation}
              >
                <AppPages />
              </BwjScrollContainer>
            </BwjViewManager>
          </section>
        </BwjSemiFlexContainer>
        <AppFooter />
      </div>
    </Router>
  );
};

export const useMainContentScrollReset = () => {
  const fn = () => EventNotificationService.dispatch(resetMainContentScrollEvent);
  return fn;
}